// RFC 5322 atext chars plus Unicode letters/digits. Dot rules checked separately below.
const LOCAL_PART_PATTERN = /^[\p{L}\p{N}!#$%&'*+/=?^_`{|}~.-]+$/u;

// Each domain label: alphanumeric start/end, hyphens only in the middle, 1-63 chars
// (matches Angular's Validators.email domain shape).
const DOMAIN_LABEL_PATTERN = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

// Same local-part/total length limits as Angular's Validators.email.
const MAX_LOCAL_PART_LENGTH = 64;
const MAX_EMAIL_LENGTH = 254;

// 2-letter labels that commonly start a compound suffix (e.g. co.uk, co.jp).
const AMBIGUOUS_TLD_PREFIXES = new Set(['co']);

/** Pragmatic email shape check for UI input, not a full RFC 5322 validator. */
export function isValidEmail(email: string): boolean {
  if (/\s/.test(email) || email.length > MAX_EMAIL_LENGTH) return false;

  const atIndex = email.indexOf('@');
  if (atIndex <= 0 || email.slice(atIndex + 1).includes('@')) return false;

  const localPart = email.slice(0, atIndex);
  if (
    localPart.length > MAX_LOCAL_PART_LENGTH ||
    localPart.startsWith('.') ||
    localPart.endsWith('.') ||
    localPart.includes('..') ||
    !LOCAL_PART_PATTERN.test(localPart)
  ) {
    return false;
  }

  const labels = email.slice(atIndex + 1).split('.');
  if (labels.length < 2 || !labels.every(label => DOMAIN_LABEL_PATTERN.test(label))) return false;

  return (labels.at(-1)?.length ?? 0) >= 2;
}

export function normalizeEmailCandidate(text: string): string {
  return text
    .trim()
    .replace(/[;,]+$/, '')
    .trim();
}

/** Extracts an email from text, e.g. `"Jane Doe" <jane@example.com>`; `null` if none valid. */
export function extractEmail(text: string): string | null {
  const trimmed = text.trim();
  if (isValidEmail(trimmed)) return trimmed;

  const candidate = /<([^<>]*)>/.exec(trimmed)?.[1]?.trim();
  return candidate && isValidEmail(candidate) ? candidate : null;
}

/** Stricter than {@link isValidEmail}: whether an address is safe to auto-commit silently. */
export function isAutoAcceptableEmail(email: string): boolean {
  if (!isValidEmail(email)) return false;

  const labels = email.slice(email.indexOf('@') + 1).split('.');
  if (labels.length >= 3) return true; // already compound, e.g. example.co.uk

  const tld = (labels.at(-1) ?? '').toLowerCase();
  return !AMBIGUOUS_TLD_PREFIXES.has(tld);
}

/** Splits pasted text into candidate chunks, keeping `"Name" <email>` whole despite its spaces. */
export function splitEmailList(text: string): string[] {
  return text
    .split(/[,;]+/)
    .flatMap(chunk => (chunk.includes('<') ? [chunk] : chunk.split(/\s+/)))
    .map(part => part.trim())
    .filter(part => part.length > 0);
}
